import React, {useEffect, useState} from 'react';
import FormikDropdown from 'src/components/formik-dropdown';
import Pill from 'src/components/Pill';
import Icon from 'shared/components/icon';
import {LoadingIcon} from 'shared/components/loading';
import {detailedTimeAgo} from 'shared/time-ago';
import {ColumnSettings} from 'src/views/admin/EditableTable/types';
import EditableTable from 'src/views/admin/EditableTable';
import Tooltip from 'src/components/tooltip';
import CheckedValue from 'src/views/admin/CheckedValue';
import {Duration, LoadState} from 'src/types/enums';
import {useLoader} from 'src/hooks/useLoader';
import {Box} from '@mui/material';
import {JobEntry, ScheduledJob} from './types';
import FinishJob from './FinishJobField';
import {ScheduleSelector} from './ScheduleSelector';
import {scheduleDescription} from './scheduleDescriptions';

interface ScheduledJobsProps {
  fetchScheduledJobTypes: () => Promise<ScheduledJob[]>;
}

function ScheduledJobs(props: ScheduledJobsProps): React.ReactElement | null {
  const [identifier, setIdentifier] = useState(new Date());
  const [scheduledJobTypes, scheduledJobsLoadState] = useLoader<ScheduledJob[]>({
    initialData: [],
    loadOptions: props.fetchScheduledJobTypes,
  });

  useEffect(() => {
    // This enables polling the job endpoint every 20 seconds
    setInterval(() => {
      setIdentifier(new Date());
    }, Duration.SECOND * 20);
  }, []);

  if (scheduledJobsLoadState !== LoadState.LOADED) {
    return null;
  }

  const titlesByTask = {};
  scheduledJobTypes.forEach(({title, task}) => (titlesByTask[task] = title));

  const ScheduleJobTypeSelector = (): React.ReactElement => (
    <FormikDropdown
      name="task"
      placeholder="Job"
      options={scheduledJobTypes.map(({title, task}) => ({_id: task, value: task, text: title}))}
    />
  );

  const columns: ColumnSettings<JobEntry>[] = [
    {
      name: 'task',
      title: 'Name',
      sortable: true,
      renderValue: function JobName(value, values): React.ReactElement {
        return (
          <Tooltip title={values.jobDescription}>
            <span>
              <Box fontSize="14px">{values.isRunning && <LoadingIcon />}</Box>
              {titlesByTask[value]}
              {values.isRunning && <br />}
              {values.isRunning && detailedTimeAgo(values.runningSince)}
              {!values.isRunning && values.lastRunError && (
                <Pill color="red">
                  <Icon iconType="times-r" /> ERROR!
                </Pill>
              )}
            </span>
          </Tooltip>
        );
      },
      FormField: ScheduleJobTypeSelector,
    },
    {
      name: 'scheduleDescription',
      title: 'Schedule',
      renderValue: (_value, values): string => scheduleDescription(values),
      FormField: ScheduleSelector,
    },
    {
      name: 'startOfLastRun',
      title: 'Last Run',
      editable: false,
    },
    {
      name: 'isRunning',
      title: 'Finish',
      editable: false,
      renderValue: FinishJob,
    },
    {
      name: 'runOnce',
      title: 'Once?',
      type: 'checkbox',
      renderValue: CheckedValue,
    },
    {
      name: 'isActive',
      title: 'Active?',
      type: 'checkbox',
      renderValue: CheckedValue,
    },
  ];

  return (
    <>
      <div className="shared-font title">Scheduled Jobs</div>
      <EditableTable
        basePath="/v1/job-entries"
        defaultSortKey="jobName"
        columns={columns}
        identifier={identifier}
        defaultValues={{isActive: true, scheduleType: 'MINUTE', min: 15}}
        disableShimmerOnReload
      />
    </>
  );
}

export default ScheduledJobs;
